import { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Search, Loader2, X, Trash2, RefreshCcw, Shield, User, Eye, Plus, AlertTriangle, Pencil, Save, KeyRound, Copy, Check, Unlock, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useEnvironment } from '@/contexts/EnvironmentContext';
import {
  listUsers, deleteUser, reactivateUser, unlockUser, createUser, updateUser, resetUserPassword,
  permanentlyDeleteUser, PermanentDeleteBlockedError, getEnterprisePlanSeats,
  UserListItem, CreateUserData, UpdateUserData, FootprintEntry, FootprintCategory, PlanSeatAvailability,
} from '@/services/usersApi';
import { listEnterprises, listTeams, Enterprise, Team } from '@/services/organizationApi';

const getErrorMessage = (err: unknown): string => {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: string } } }).response;
    return response?.data?.error || '';
  }
  if (err && typeof err === 'object' && 'message' in err) {
    return (err as { message: string }).message;
  }
  return '';
};

const getInitials = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
  const first = firstName || '';
  const last = lastName || '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '??';
};

const getRoleInfo = (roles: string[] | null | undefined) => {
  const safeRoles = roles || [];
  if (safeRoles.includes('super_admin')) return { label: 'Super Admin', color: 'bg-purple-100 text-purple-700', Icon: Shield };
  if (safeRoles.includes('admin')) return { label: 'Admin', color: 'bg-indigo-100 text-indigo-700', Icon: Shield };
  if (safeRoles.includes('viewer')) return { label: 'Lecteur', color: 'bg-gray-100 text-gray-600', Icon: Eye };
  return { label: 'Utilisateur', color: 'bg-blue-100 text-blue-700', Icon: User };
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

const FOOTPRINT_LABELS: Record<FootprintCategory, string> = {
  problems: 'Problèmes',
  macroplans: 'Plannings macro',
  macroplan_notes: 'Notes de planning',
  support_conversations: 'Conversations de support',
  hansei_sessions: 'Sessions Hansei',
  hansei_items: 'Items Hansei',
  hansei_votes: 'Votes Hansei',
  roles_granted: 'Rôles octroyés à d’autres',
  team_app_grants: 'Permissions d’app accordées',
  team_chart_grants: 'Permissions de chart accordées',
};

export default function UsersPage() {
  const { current } = useEnvironment();

  const [users, setUsers] = useState<UserListItem[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [enterpriseFilter, setEnterpriseFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<UserListItem | null>(null);
  const [reactivateDialog, setReactivateDialog] = useState<UserListItem | null>(null);
  const [permanentDeleteDialog, setPermanentDeleteDialog] = useState<UserListItem | null>(null);
  const [permanentDeleteEmailInput, setPermanentDeleteEmailInput] = useState('');
  const [permanentDeleteBlockedFootprint, setPermanentDeleteBlockedFootprint] = useState<FootprintEntry[] | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserData>({
    email: '', password: '', first_name: '', last_name: '', role: 'user',
  });
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<UpdateUserData>({});
  const [editLoading, setEditLoading] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState<UserListItem | null>(null);
  const [resetPasswordResult, setResetPasswordResult] = useState<string | null>(null);
  const [resetPasswordCopied, setResetPasswordCopied] = useState(false);
  const [editEnterprises, setEditEnterprises] = useState<Enterprise[]>([]);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [createTeams, setCreateTeams] = useState<Team[]>([]);
  // Multi-plan (PR3) : si l'enterprise sélectionnée a > 1 plan acheté, on
  // doit forcer un sélecteur de plan obligatoire.
  const [createPlanSeats, setCreatePlanSeats] = useState<PlanSeatAvailability[]>([]);

  const enterpriseNames = useMemo(() => {
    const names = new Set<string>();
    for (const user of users) {
      if (user.enterprise_name) names.add(user.enterprise_name);
    }
    return Array.from(names).sort();
  }, [users]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listUsers(showDeleted);
      setUsers(Array.isArray(data) ? data : []);
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Échec du chargement des utilisateurs');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [showDeleted]);

  // Load users on mount and when showDeleted toggles
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Reset edit mode when selected user changes
  useEffect(() => {
    setEditMode(false);
    setEditForm({});
  }, [selectedUser?.id]);

  // Load enterprises when create dialog opens
  useEffect(() => {
    if (showCreateDialog) {
      listEnterprises().then(setEnterprises).catch(() => setEnterprises([]));
    }
  }, [showCreateDialog]);

  // Load teams when enterprise changes in create form
  useEffect(() => {
    if (createForm.enterprise_id) {
      listTeams(createForm.enterprise_id).then(setCreateTeams).catch(() => setCreateTeams([]));
    } else {
      setCreateTeams([]);
    }
  }, [createForm.enterprise_id]);

  // Multi-plan : load purchased plans when enterprise changes. Si > 1 plan,
  // le sélecteur devient obligatoire (cohérent avec userController PR3).
  useEffect(() => {
    if (createForm.enterprise_id) {
      getEnterprisePlanSeats(createForm.enterprise_id)
        .then(setCreatePlanSeats)
        .catch(() => setCreatePlanSeats([]));
    } else {
      setCreatePlanSeats([]);
    }
    // Reset le plan sélectionné quand on change d'enterprise pour éviter
    // d'envoyer un plan_tier_id qui n'appartient pas à la nouvelle enterprise.
    setCreateForm((prev) => ({ ...prev, plan_tier_id: undefined }));
  }, [createForm.enterprise_id]);

  // Load enterprises when edit mode is entered
  useEffect(() => {
    if (editMode) {
      listEnterprises().then(setEditEnterprises).catch(() => setEditEnterprises([]));
    }
  }, [editMode]);

  const filteredUsers = (users || []).filter((user) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery ||
      (user.first_name || '').toLowerCase().includes(searchLower) ||
      (user.last_name || '').toLowerCase().includes(searchLower) ||
      (user.email || '').toLowerCase().includes(searchLower) ||
      (user.enterprise_name || '').toLowerCase().includes(searchLower);

    const roles = user.roles || [];
    const matchesRole = roleFilter === 'all' ||
      (roleFilter === 'admin' && (roles.includes('admin') || roles.includes('super_admin'))) ||
      (roleFilter === 'user' && !roles.includes('admin') && !roles.includes('super_admin') && !roles.includes('viewer')) ||
      (roleFilter === 'viewer' && roles.includes('viewer'));

    const matchesEnterprise = enterpriseFilter === 'all' || user.enterprise_name === enterpriseFilter;

    return matchesSearch && matchesRole && matchesEnterprise;
  });

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      setActionLoading(true);
      await deleteUser(deleteDialog.id);
      toast.success(`Utilisateur ${deleteDialog.first_name} ${deleteDialog.last_name} supprimé`);
      setDeleteDialog(null);
      setSelectedUser(null);
      await loadUsers();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) || 'Échec de la suppression de l’utilisateur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!reactivateDialog) return;
    try {
      setActionLoading(true);
      await reactivateUser(reactivateDialog.id);
      toast.success(`Utilisateur ${reactivateDialog.first_name} ${reactivateDialog.last_name} réactivé`);
      setReactivateDialog(null);
      setSelectedUser(null);
      await loadUsers();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) || 'Échec de la réactivation de l’utilisateur');
    } finally {
      setActionLoading(false);
    }
  };

  const openPermanentDeleteDialog = (user: UserListItem) => {
    setPermanentDeleteDialog(user);
    setPermanentDeleteEmailInput('');
    setPermanentDeleteBlockedFootprint(null);
  };

  const closePermanentDeleteDialog = () => {
    setPermanentDeleteDialog(null);
    setPermanentDeleteEmailInput('');
    setPermanentDeleteBlockedFootprint(null);
  };

  const handlePermanentDelete = async () => {
    if (!permanentDeleteDialog) return;
    // The user's email after soft-delete is the anonymized placeholder, not their original.
    // We require typing the *original* email (previous_email) — but since it's not exposed,
    // we use a deterministic confirmation phrase instead.
    try {
      setActionLoading(true);
      setPermanentDeleteBlockedFootprint(null);
      await permanentlyDeleteUser(permanentDeleteDialog.id);
      toast.success(`Utilisateur ${permanentDeleteDialog.first_name} ${permanentDeleteDialog.last_name} supprimé définitivement`);
      closePermanentDeleteDialog();
      setSelectedUser(null);
      await loadUsers();
    } catch (err: unknown) {
      if (err instanceof PermanentDeleteBlockedError && err.details.reason === 'has_data' && err.details.footprint) {
        setPermanentDeleteBlockedFootprint(err.details.footprint);
      } else if (err instanceof PermanentDeleteBlockedError) {
        toast.error(err.message);
      } else {
        toast.error(getErrorMessage(err) || 'Échec de la suppression définitive');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.email || !createForm.password || !createForm.first_name || !createForm.last_name) {
      toast.error('Tous les champs sont obligatoires');
      return;
    }
    if (!createForm.enterprise_id) {
      toast.error('L’entreprise est obligatoire');
      return;
    }
    if (!createForm.team_id) {
      toast.error('L’équipe est obligatoire');
      return;
    }
    // Multi-plan : si l'enterprise a > 1 plan, plan_tier_id est requis.
    if (createPlanSeats.length > 1 && !createForm.plan_tier_id) {
      toast.error('Sélectionnez un plan d\'affectation (cette enterprise a plusieurs plans).');
      return;
    }
    try {
      setActionLoading(true);
      await createUser(createForm);
      toast.success('Utilisateur créé avec succès');
      setShowCreateDialog(false);
      setCreateForm({ email: '', password: '', first_name: '', last_name: '', role: 'user' });
      setCreateTeams([]);
      setCreatePlanSeats([]);
      await loadUsers();
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      if (message.includes('Email already exists')) {
        toast.error('Un utilisateur avec cet email existe déjà');
      } else {
        toast.error(message || 'Échec de la création de l’utilisateur');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleEnterEditMode = () => {
    if (!selectedUser) return;
    const currentRole = (selectedUser.roles || []).find(r => ['admin', 'viewer', 'user'].includes(r)) || 'user';
    setEditForm({
      first_name: selectedUser.first_name,
      last_name: selectedUser.last_name,
      email: selectedUser.email,
      role: currentRole,
      enterprise_id: selectedUser.enterprise_id || null,
    });
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditForm({});
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;
    try {
      setEditLoading(true);
      await updateUser(selectedUser.id, editForm);
      toast.success('Utilisateur mis à jour avec succès');
      setEditMode(false);
      setEditForm({});
      await loadUsers();
      // Update selectedUser with new data
      const updatedUsers = await listUsers(showDeleted);
      const updated = updatedUsers.find(u => u.id === selectedUser.id);
      if (updated) setSelectedUser(updated);
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      if (message.includes('Email already exists')) {
        toast.error('Un utilisateur avec cet email existe déjà');
      } else {
        toast.error(message || 'Échec de la mise à jour de l’utilisateur');
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordDialog) return;
    try {
      setActionLoading(true);
      const { temporaryPassword } = await resetUserPassword(resetPasswordDialog.id);
      setResetPasswordResult(temporaryPassword);
      setResetPasswordCopied(false);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) || 'Échec de la réinitialisation du mot de passe');
      setResetPasswordDialog(null);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyPassword = () => {
    if (!resetPasswordResult) return;
    navigator.clipboard.writeText(resetPasswordResult).then(() => {
      setResetPasswordCopied(true);
      setTimeout(() => setResetPasswordCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-indigo-500" />
          <h2 className="text-xl font-semibold text-gray-800">Utilisateurs</h2>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            ({ blue: 'bg-blue-100 text-blue-700', amber: 'bg-amber-100 text-amber-700', red: 'bg-red-100 text-red-700' })[current.color]
          }`}>
            {current.label}
          </span>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          Créer un utilisateur
        </button>
      </div>

      {/* Production warning */}
      {current.color === 'red' && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Vous gérez l&rsquo;environnement de <strong>production</strong>. Les changements affecteront de vrais utilisateurs.
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center justify-between">
          {error}
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-50 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un utilisateur..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="all">Tous les rôles</option>
          <option value="admin">Admin</option>
          <option value="user">Utilisateur</option>
          <option value="viewer">Lecteur</option>
        </select>

        <select
          value={enterpriseFilter}
          onChange={(e) => setEnterpriseFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="all">Toutes les entreprises</option>
          {enterpriseNames.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          Afficher les supprimés
        </label>

        <button
          onClick={loadUsers}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          title="Actualiser"
        >
          <RefreshCcw className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users List */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-700">
              Utilisateurs ({filteredUsers.length})
            </h3>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-12">Aucun utilisateur trouvé</p>
          ) : (
            <div className="space-y-2 max-h-150 overflow-y-auto">
              {filteredUsers.map((user) => {
                const roleInfo = getRoleInfo(user.roles);
                const isDeleted = !user.is_active;
                const isSelected = selectedUser?.id === user.id;

                return (
                  <div
                    key={user.id}
                    className={`group flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-indigo-50 border border-indigo-200'
                        : isDeleted
                        ? 'bg-gray-50 border border-gray-200'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                    onClick={() => setSelectedUser(user)}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                      isDeleted ? 'bg-gray-200 text-gray-500' : 'bg-indigo-100 text-indigo-700'
                    }`}>
                      {getInitials(user.first_name, user.last_name)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium text-sm ${isDeleted ? 'text-gray-400' : 'text-gray-900'}`}>
                          {user.first_name || ''} {user.last_name || ''}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleInfo.color}`}>
                          {roleInfo.label}
                        </span>
                        {user.enterprise_name && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 truncate max-w-35">
                            {user.enterprise_name}
                          </span>
                        )}
                        {isDeleted && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            Supprimé
                          </span>
                        )}
                        {user.status === 'locked' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                            Verrouillé
                          </span>
                        )}
                      </div>
                      <p className={`text-sm truncate ${isDeleted ? 'text-gray-400' : 'text-gray-500'}`}>
                        {user.email || ''}
                      </p>
                    </div>

                    <div className="hidden group-hover:flex items-center gap-1">
                      {isDeleted ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); setReactivateDialog(user); }}
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                          title="Réactiver"
                        >
                          <RefreshCcw className="w-4 h-4" />
                        </button>
                      ) : (
                        <>
                          {user.status === 'locked' && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await unlockUser(user.id);
                                  toast.success(`${user.first_name} ${user.last_name} déverrouillé`);
                                  await loadUsers();
                                } catch (err: unknown) {
                                  toast.error(getErrorMessage(err) || 'Échec du déverrouillage');
                                }
                              }}
                              className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded"
                              title="Déverrouiller"
                            >
                              <Unlock className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteDialog(user); }}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* User Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-700">Détails</h3>
            {selectedUser && selectedUser.is_active && !editMode && (
              <button
                onClick={handleEnterEditMode}
                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                title="Modifier l’utilisateur"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>

          {selectedUser ? (
            editMode ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Prénom</label>
                    <input
                      type="text"
                      value={editForm.first_name || ''}
                      onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Nom</label>
                    <input
                      type="text"
                      value={editForm.last_name || ''}
                      onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Email</label>
                  <input
                    type="email"
                    value={editForm.email || ''}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Rôle</label>
                  <select
                    value={editForm.role || 'user'}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="user">Utilisateur</option>
                    <option value="viewer">Lecteur</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">Entreprise</label>
                  <select
                    value={editForm.enterprise_id || ''}
                    onChange={(e) => setEditForm({ ...editForm, enterprise_id: e.target.value || null })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Aucune entreprise</option>
                    {editEnterprises.map(ent => (
                      <option key={ent.id} value={ent.id}>{ent.name}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-100">
                  <button
                    onClick={handleCancelEdit}
                    disabled={editLoading}
                    className="flex-1 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={editLoading}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Enregistrer
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center pb-4 border-b border-gray-100">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center text-xl font-semibold ${
                    selectedUser.is_active ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {getInitials(selectedUser.first_name, selectedUser.last_name)}
                  </div>
                  <h4 className="mt-3 font-semibold text-gray-900">
                    {selectedUser.first_name || ''} {selectedUser.last_name || ''}
                  </h4>
                  <p className="text-sm text-gray-500">{selectedUser.email || ''}</p>
                </div>

                <div className="space-y-3">
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Rôle</span>
                    <p className="mt-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleInfo(selectedUser.roles).color}`}>
                        {getRoleInfo(selectedUser.roles).label}
                      </span>
                    </p>
                  </div>

                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Statut</span>
                    <p className="mt-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        selectedUser.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {selectedUser.is_active ? 'Actif' : 'Supprimé'}
                      </span>
                    </p>
                  </div>

                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Dernière connexion</span>
                    <p className="mt-1 text-sm text-gray-700">
                      {selectedUser.last_login_at ? formatDate(selectedUser.last_login_at) : 'Jamais'}
                    </p>
                  </div>

                  {selectedUser.enterprise_name && (
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-wide">Entreprise</span>
                      <p className="mt-1 text-sm text-gray-700">{selectedUser.enterprise_name}</p>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-gray-100 space-y-2">
                  {selectedUser.status === 'locked' && (
                    <button
                      onClick={async () => {
                        try {
                          await unlockUser(selectedUser.id);
                          toast.success(`${selectedUser.first_name} ${selectedUser.last_name} déverrouillé`);
                          setSelectedUser(null);
                          await loadUsers();
                        } catch (err: unknown) {
                          toast.error(getErrorMessage(err) || 'Échec du déverrouillage');
                        }
                      }}
                      className="w-full px-4 py-2 text-orange-600 hover:bg-orange-50 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <Unlock className="w-4 h-4" />
                      Déverrouiller l&rsquo;utilisateur
                    </button>
                  )}
                  {selectedUser.is_active && (
                    <button
                      onClick={() => { setResetPasswordDialog(selectedUser); setResetPasswordResult(null); }}
                      className="w-full px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <KeyRound className="w-4 h-4" />
                      Réinitialiser le mot de passe
                    </button>
                  )}
                  {selectedUser.is_active ? (
                    <button
                      onClick={() => setDeleteDialog(selectedUser)}
                      className="w-full px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
                    >
                      Supprimer l&rsquo;utilisateur
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => setReactivateDialog(selectedUser)}
                        className="w-full px-4 py-2 text-green-600 hover:bg-green-50 rounded-lg text-sm font-medium transition-colors"
                      >
                        Réactiver l&rsquo;utilisateur
                      </button>
                      <button
                        onClick={() => openPermanentDeleteDialog(selectedUser)}
                        className="w-full px-4 py-2 text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <ShieldAlert className="w-4 h-4" />
                        Supprimer définitivement
                      </button>
                    </>
                  )}
                </div>
              </div>
            )
          ) : (
            <p className="text-sm text-gray-500 text-center py-8">
              Sélectionnez un utilisateur pour voir les détails
            </p>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-2">Supprimer l&rsquo;utilisateur</h3>
            <p className="text-gray-600 mb-4">
              Confirmer la suppression de <strong>{deleteDialog.first_name} {deleteDialog.last_name}</strong> ?
              Suppression réversible : l&rsquo;utilisateur est désactivé et son adresse email est libérée pour pouvoir être réutilisée par une autre entreprise.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteDialog(null)}
                disabled={actionLoading}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Confirmation Dialog */}
      {reactivateDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-2">Réactiver l&rsquo;utilisateur</h3>
            <p className="text-gray-600 mb-4">
              Confirmer la réactivation de <strong>{reactivateDialog.first_name} {reactivateDialog.last_name}</strong> ?
              L&rsquo;adresse email d&rsquo;origine sera restaurée. Si elle est désormais utilisée par un autre compte, la réactivation sera refusée.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setReactivateDialog(null)}
                disabled={actionLoading}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleReactivate}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Réactiver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation Dialog */}
      {permanentDeleteDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg mx-4 w-full">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="w-5 h-5 text-red-600 shrink-0" />
              <h3 className="text-lg font-semibold text-red-700">Suppression définitive</h3>
            </div>

            {permanentDeleteBlockedFootprint ? (
              <div className="space-y-3">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  Suppression définitive impossible : cet utilisateur a créé du contenu sur la plateforme.
                  Conserver le compte en suppression simple (ce qui libère déjà l&rsquo;email).
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Données associées :</p>
                  <ul className="text-sm text-gray-600 space-y-1 ml-2">
                    {permanentDeleteBlockedFootprint.map((entry) => (
                      <li key={entry.category} className="flex justify-between">
                        <span>{FOOTPRINT_LABELS[entry.category] ?? entry.category}</span>
                        <span className="font-mono text-gray-800">{entry.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex justify-end pt-2">
                  <button
                    onClick={closePermanentDeleteDialog}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-700">
                  Cette action <strong>est irréversible</strong>. Le compte de{' '}
                  <strong>{permanentDeleteDialog.first_name} {permanentDeleteDialog.last_name}</strong>{' '}
                  sera supprimé de la base de données. Possible uniquement si l&rsquo;utilisateur n&rsquo;a créé aucun
                  contenu (problèmes, plannings, hansei, etc.). Sinon, conserver la suppression simple.
                </p>
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <label className="block text-xs font-medium text-red-700 mb-1">
                    Pour confirmer, saisir <code className="font-mono bg-white px-1 rounded">SUPPRIMER</code>
                  </label>
                  <input
                    type="text"
                    value={permanentDeleteEmailInput}
                    onChange={(e) => setPermanentDeleteEmailInput(e.target.value)}
                    placeholder="SUPPRIMER"
                    className="w-full px-3 py-1.5 border border-red-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    autoFocus
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={closePermanentDeleteDialog}
                    disabled={actionLoading}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handlePermanentDelete}
                    disabled={actionLoading || permanentDeleteEmailInput !== 'SUPPRIMER'}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Supprimer définitivement
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reset Password Dialog */}
      {resetPasswordDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Réinitialiser le mot de passe</h3>
              <button
                onClick={() => { setResetPasswordDialog(null); setResetPasswordResult(null); }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {resetPasswordResult ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Un mot de passe temporaire a été généré pour <strong>{resetPasswordDialog.first_name} {resetPasswordDialog.last_name}</strong>.
                  Partagez-le de façon sécurisée — l&rsquo;utilisateur devra le changer à sa prochaine connexion.
                </p>
                <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <code className="flex-1 text-sm font-mono break-all">{resetPasswordResult}</code>
                  <button
                    onClick={handleCopyPassword}
                    className="shrink-0 p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                    title="Copier"
                  >
                    {resetPasswordCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <button
                  onClick={() => { setResetPasswordDialog(null); setResetPasswordResult(null); }}
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
                >
                  Terminé
                </button>
              </div>
            ) : (
              <>
                <p className="text-gray-600 mb-4 text-sm">
                  Un nouveau mot de passe temporaire sera généré pour <strong>{resetPasswordDialog.first_name} {resetPasswordDialog.last_name}</strong> et son mot de passe actuel sera invalidé.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setResetPasswordDialog(null)}
                    disabled={actionLoading}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleResetPassword}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Générer le mot de passe
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create User Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Créer un utilisateur</h3>
              <button onClick={() => setShowCreateDialog(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {current.color === 'red' && (
              <div className="flex items-center gap-2 p-2 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                Création d&rsquo;utilisateur en <strong>production</strong>
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                  <input
                    type="text"
                    value={createForm.first_name}
                    onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Jean"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    type="text"
                    value={createForm.last_name}
                    onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Dupont"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="jean.dupont@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe *</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="mt-1 text-xs text-gray-500">Min. 12 caractères : majuscule, minuscule, chiffre, caractère spécial</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="user">Utilisateur</option>
                  <option value="viewer">Lecteur</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise *</label>
                <select
                  value={createForm.enterprise_id || ''}
                  onChange={(e) => setCreateForm({ ...createForm, enterprise_id: e.target.value || undefined, team_id: undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Sélectionner une entreprise</option>
                  {enterprises.map(ent => (
                    <option key={ent.id} value={ent.id}>{ent.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Équipe *</label>
                <select
                  value={createForm.team_id || ''}
                  onChange={(e) => setCreateForm({ ...createForm, team_id: e.target.value || undefined })}
                  disabled={!createForm.enterprise_id}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">{createForm.enterprise_id ? 'Sélectionner une équipe' : 'Sélectionner d’abord une entreprise'}</option>
                  {createTeams.map(team => (
                    <option key={team.id} value={team.id}>
                      {'—'.repeat(team.depth || 0)} {team.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Multi-plan (PR3) : si l'enterprise a >1 plan, sélecteur obligatoire.
                Si exactement 1 plan, on peut le pré-affecter implicitement côté
                backend (fallback subscription.plan_tier_id) — pas de selecteur ici. */}
            {createPlanSeats.length > 1 && (() => {
              const allSaturated = createPlanSeats.every((p) => p.remaining <= 0);
              const selected = createForm.plan_tier_id
                ? createPlanSeats.find((p) => p.planTierId === createForm.plan_tier_id) ?? null
                : null;
              const selectedSaturated = selected ? selected.remaining <= 0 : false;
              const missing = !createForm.plan_tier_id;
              return (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan d'affectation *
                    {missing && (
                      <span className="ml-1 text-amber-700 font-normal">— non sélectionné</span>
                    )}
                  </label>
                  <select
                    value={createForm.plan_tier_id || ''}
                    onChange={(e) => setCreateForm({ ...createForm, plan_tier_id: e.target.value || undefined })}
                    disabled={allSaturated}
                    aria-invalid={missing || selectedSaturated}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 ${
                      missing ? 'border-amber-300 bg-amber-50/40' : 'border-gray-300'
                    }`}
                  >
                    <option value="">— Sélectionner un plan —</option>
                    {createPlanSeats.map((p) => (
                      <option key={p.planTierId} value={p.planTierId} disabled={p.remaining <= 0}>
                        {p.planName} ({p.remaining}/{p.purchased} sièges restants{p.remaining <= 0 ? ' — saturé' : ''})
                      </option>
                    ))}
                  </select>
                  {allSaturated && (
                    <p className="mt-1 text-xs text-red-600">
                      Tous les plans sont saturés. Augmentez les sièges du contrat ou désactivez des utilisateurs.
                    </p>
                  )}
                  {selectedSaturated && !allSaturated && (
                    <p className="mt-1 text-xs text-red-600">
                      Ce plan est saturé. Choisissez un autre plan ou augmentez ses sièges.
                    </p>
                  )}
                </div>
              );
            })()}

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowCreateDialog(false)}
                disabled={actionLoading}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleCreate}
                disabled={
                  actionLoading
                  || !createForm.enterprise_id
                  || !createForm.team_id
                  || (createPlanSeats.length > 1 && !createForm.plan_tier_id)
                }
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Créer l&rsquo;utilisateur
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
